import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { Team } from '../types';
import { initializeDepthStrings } from '../data/sportRulesets';

interface UseDepthStringsProps {
  currentTeam: Team | null;
  activeTab: string;
}

export const useDepthStrings = ({ currentTeam, activeTab }: UseDepthStringsProps) => {
  const [depthStrings, setDepthStrings] = useState<Record<string, {id: number, name: string, active: boolean}[]>>({
    Offense: [
      {id: 1, name: '1st String', active: true},
      {id: 2, name: '2nd String', active: true},
      {id: 3, name: '3rd String', active: true}
    ],
    Defense: [
      {id: 1, name: '1st String', active: true},
      {id: 2, name: '2nd String', active: true},
      {id: 3, name: '3rd String', active: true}
    ],
    'Special Teams': [
      {id: 1, name: '1st Unit', active: true},
      {id: 2, name: '2nd Unit', active: true}
    ],
    'Lineup': [],
    'Fielding': [
      {id: 1, name: 'Starters', active: true},
      {id: 2, name: 'Backups', active: true}
    ]
  });

  const [nextDepthStringId, setNextDepthStringId] = useState<Record<string, number>>({
    Offense: 4,
    Defense: 4, 
    'Special Teams': 3
  });

  const [editingDepthString, setEditingDepthString] = useState<number | null>(null);
  const [editedDepthStringName, setEditedDepthStringName] = useState<string>('');

  // Helper to generate a default name for new depth strings
  const getDefaultDepthStringName = useCallback((id: number, tab: string): string => {
    if (tab === 'Offense' || tab === 'Defense') {
      if (id <= 3) {
        return `${id}${getOrdinalSuffix(id)} String`;
      } else {
        return `${id}${getOrdinalSuffix(id)} String`;
      }
    } else if (tab === 'Special Teams') {
      return `${id}${getOrdinalSuffix(id)} Unit`;
    }
    
    return `Group ${id}`;
  }, []);

  // Helper function to get ordinal suffix (1st, 2nd, 3rd, etc.)
  const getOrdinalSuffix = useCallback((num: number): string => {
    const j = num % 10;
    const k = num % 100;
    
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  }, []);

  // Function to load depth strings from the database
  const loadDepthStrings = useCallback(async () => {
    if (!currentTeam) return;
    
    try {
      const { data, error } = await supabase
        .from('teams')
        .select('depth_strings')
        .eq('id', currentTeam.id)
        .single();
      
      if (error) throw error;
      
      if (data?.depth_strings) {
        console.log('[useDepthStrings] Loaded saved depth strings:', data.depth_strings);
        setDepthStrings(data.depth_strings);
      } else {
        // No saved depth strings, use defaults
        const initialDepthStrings = initializeDepthStrings(currentTeam.sport);
        setDepthStrings(initialDepthStrings);
      }
    } catch (err) {
      console.error('Error loading depth strings:', err);
      // Fall back to defaults
      const initialDepthStrings = initializeDepthStrings(currentTeam.sport);
      setDepthStrings(initialDepthStrings);
    }
  }, [currentTeam]);

  // Function to save depth strings config to the database
  const persistDepthStrings = useCallback(async (newDepthStrings: typeof depthStrings) => {
    if (!currentTeam) return;
    
    try {
      console.log('[useDepthStrings] Saving depth strings to database:', newDepthStrings);
      const { error } = await supabase
        .from('teams')
        .update({ depth_strings: newDepthStrings })
        .eq('id', currentTeam.id);
      
      if (error) throw error;
      console.log('[useDepthStrings] Successfully saved depth strings');
    } catch (err) {
      console.error('Error saving depth strings:', err);
      toast.error('Failed to save depth string changes');
    }
  }, [currentTeam]);

  // Function to handle editing depth string names
  const handleDepthStringEdit = useCallback((id: number) => {
    const depthString = depthStrings[activeTab].find(ds => ds.id === id);
    if (depthString) {
      setEditingDepthString(id);
      setEditedDepthStringName(depthString.name);
    }
  }, [depthStrings, activeTab]);

  // Function to save edited depth string name
  const saveDepthStringEdit = useCallback(async () => {
    if (editingDepthString === null) return;
    
    // Validate that the name is not empty
    if (!editedDepthStringName.trim()) {
      toast.error('Depth string name cannot be empty');
      return;
    }
    const newDepthStrings = {
      ...depthStrings,
      [activeTab]: depthStrings[activeTab].map(ds =>
        ds.id === editingDepthString ? { ...ds, name: editedDepthStringName } : ds
      )
    };
    setDepthStrings(newDepthStrings);
    setEditingDepthString(null);
    setEditedDepthStringName('');
    toast.success('Depth string name updated');
    await persistDepthStrings(newDepthStrings);
  }, [editingDepthString, editedDepthStringName, depthStrings, activeTab, persistDepthStrings]);

  // Function to toggle depth string active/inactive
  const toggleDepthStringActive = useCallback(async (id: number) => {
    // Make sure we can't disable all depth strings
    const activeCount = depthStrings[activeTab].filter(ds => ds.active).length;
    const isActive = depthStrings[activeTab].find(ds => ds.id === id)?.active;
    if (activeCount === 1 && isActive) {
      toast.error('At least one depth string must be active');
      return;
    }
    const newDepthStrings = {
      ...depthStrings,
      [activeTab]: depthStrings[activeTab].map(ds =>
        ds.id === id ? { ...ds, active: !ds.active } : ds
      )
    };
    setDepthStrings(newDepthStrings);
    await persistDepthStrings(newDepthStrings);
  }, [depthStrings, activeTab, persistDepthStrings]);

  // Function to add a new depth string
  const addNewDepthString = useCallback(() => {
    if (!nextDepthStringId[activeTab]) {
      // Initialize new ID if it doesn't exist yet
      setNextDepthStringId(prev => ({
        ...prev,
        [activeTab]: Math.max(...depthStrings[activeTab]?.map(ds => ds.id) || [0]) + 1
      }));
      return;
    }
    
    const newId = nextDepthStringId[activeTab];
    const newName = getDefaultDepthStringName(newId, activeTab);
    
    setDepthStrings(prev => ({
      ...prev,
      [activeTab]: [
        ...(prev[activeTab] || []),
        { id: newId, name: newName, active: true }
      ]
    }));
    
    // Update the next available ID
    setNextDepthStringId(prev => ({
      ...prev,
      [activeTab]: prev[activeTab] + 1
    }));
    
    toast.success('New depth string added');
  }, [nextDepthStringId, activeTab, depthStrings, getDefaultDepthStringName]);

  // Load depth strings when team changes
  useEffect(() => {
    if (currentTeam) {
      loadDepthStrings();
    }
  }, [currentTeam, loadDepthStrings]);

  return {
    depthStrings,
    editingDepthString,
    editedDepthStringName,
    setEditedDepthStringName,
    handleDepthStringEdit,
    saveDepthStringEdit,
    toggleDepthStringActive,
    addNewDepthString,
    loadDepthStrings,
    setDepthStrings
  };
}; 